<script>
import { mapGetters } from 'vuex'
import dayjs from 'dayjs'
import DateFormatToken from '@/constants/date/DateFormatToken'
import { DurationUnits } from '@/constants/date/DurationUnits'
import LocationRestriction from '@/models/locationRestriction/locationRestriction'

export default {
  name: 'LocationRestrictionExtender',
  props: {
    selected: {
      type: LocationRestriction,
      default: () => new LocationRestriction(),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dateMinimum: null,
      dateSelected: new Date(),
      restrictionEvents: [],
    }
  },
  computed: {
    ...mapGetters({
      restrictionsByLocationId: 'locationRestriction/restrictionsByLocationId',
    }),
    locationName() {
      return this.selected?.locationName
    },
    locationId() {
      return this.selected?.locationID
    },
    allRestrictionsAtLocationOfSameType() {
      return this.restrictionsByLocationId(this.locationId)
        .filter((a) => a.restriction === this.selected.restriction)
        .sort((a, b) => new Date(a.dateUntil) - new Date(b.dateUntil))
    },
    startOfNextRestrictionAtLocation() {
      const selectedIndex = this.allRestrictionsAtLocationOfSameType.findIndex(
        (a) => a.id === this.selected.id
      )
      const next = this.allRestrictionsAtLocationOfSameType[selectedIndex + 1]
      return next?.dateFrom || null
    },
    selectedRestrictionDates() {
      const dates = []
      const startDate = dayjs(this.selected.dateFrom)
      const endDate = dayjs(this.selected.dateUntil)
      const daysBetween = endDate.diff(startDate, DurationUnits.DAY)
      for (let i = 0; i <= daysBetween; i++) {
        dates.push(
          startDate
            .add(i, DurationUnits.DAY)
            .locale(this.$i18n.locale)
            .format(DateFormatToken.YYYYMMDD)
        )
      }
      return dates
    },
    isEventLegendWarningShowing() {
      return this.allRestrictionsAtLocationOfSameType.length > 1
    },
  },
  watch: {
    selected: {
      deep: true,
      immediate: true,
      handler() {
        if (this.selected) this.loadDetailsForSelectedRestriction()
      },
    },
  },
  methods: {
    loadDetailsForSelectedRestriction() {
      const markedDates = []
      for (const restriction of this.allRestrictionsAtLocationOfSameType) {
        const startDate = dayjs(restriction.dateFrom)
        const endDate = dayjs(restriction.dateUntil)
        const daysBetween = endDate.diff(startDate, 'day')
        for (let i = 0; i <= daysBetween; i++) {
          const d = startDate
            .add(i, 'day')
            .locale(this.$i18n.locale)
            .format(DateFormatToken.YYYYMMDD)
          markedDates.push(d)
        }
      }

      this.restrictionEvents = markedDates
      this.dateSelected = dayjs(this.selected.dateUntil)
        .add(1, 'day')
        .locale(this.$i18n.locale)
        .format(DateFormatToken.YYYYMMDD)
      this.dateMinimum = dayjs(
        this.allRestrictionsAtLocationOfSameType[0].dateFrom
      )
        .locale(this.$i18n.locale)
        .format(DateFormatToken.YYYYMMDD)
    },
    restrictionEventColor(date) {
      return this.selectedRestrictionDates.includes(date)
        ? 'green'
        : 'red lighten-3'
    },
    allowedDatesForCalendar(dateStr) {
      const date = dayjs(dateStr)
      return this.isDateAllowed(date)
    },
    /** @param date {Date} */
    isDateAllowed(date) {
      if (this.startOfNextRestrictionAtLocation) {
        return (
          date >= this.selected.dateUntil &&
          date < this.startOfNextRestrictionAtLocation
        )
      }
      return date > this.selected.dateUntil
    },
    handleSave() {
      this.$emit(
        'save',
        Array.isArray(this.dateSelected)
          ? this.dateSelected[0]
          : this.dateSelected
      )
    },
    handleCancel() {
      this.$emit('cancel')
    },
  },
}
</script>

<template>
  <v-card>
    <v-card-title>{{ locationName }}</v-card-title>
    <v-card-subtitle>
      {{ $t('locationRestrictions.extend.subtitle') }}

      <div
        v-if="isEventLegendWarningShowing"
        id="extender-event-warning"
        data-testid="extender-event-warning"
      >
        <div>
          <v-icon color="green" large>mdi-circle-small</v-icon>
          {{ $t('locationRestrictions.extend.legendCurrent') }}
        </div>
        <div>
          <v-icon color="red lighten-3" large>mdi-circle-small</v-icon>
          {{ $t('locationRestrictions.extend.legendUpcoming') }}
        </div>
        <v-alert dense text type="info">
          {{ $t('locationRestrictions.extend.alertInfo') }}
        </v-alert>
      </div>
    </v-card-subtitle>

    <v-card-text>
      <v-date-picker
        v-model="dateSelected"
        data-testid="date-picker"
        full-width
        :min="dateMinimum"
        :events="restrictionEvents"
        :event-color="restrictionEventColor"
        :allowed-dates="allowedDatesForCalendar"
      >
      </v-date-picker>
    </v-card-text>

    <v-card-actions>
      <v-btn text depressed @click="handleCancel">
        {{ $t('locationRestrictions.extend.cancelBtn') }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        data-testid="extender-save-btn"
        color="primary"
        text
        depressed
        :loading="loading"
        @click="handleSave"
      >
        {{ $t('locationRestrictions.extend.saveBtn') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
